import React from 'react';
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSave } from '@fortawesome/free-solid-svg-icons';
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";

const ProfileInfo = ({ 
  firstName, 
  lastName, 
  isLoading, 
  theme, 
  onSave 
}) => {
  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, "Enter atleast 2 characters")
      .required("Please enter the first name")
      .matches(/^(\S+$)/g, "First Name cannot contain blankspaces")
      .matches(/^[a-zA-Z]+$/, "Only alphabetic characters allowed"),
    last_name: Yup.string()
      .min(2, "Enter atleast 2 characters")
      .required("Please enter the last name")
      .matches(/^(\S+$)/g, "Last Name cannot contain blankspaces")
      .matches(/^[a-zA-Z]+$/, "Only alphabetic characters allowed"),
  });

  const styles = {
    card: {
      background: theme?.background?.card || theme?.backgrounds?.card,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
      marginBottom: '1.5rem',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem',
    },
    form: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    inputGroup: {
      marginBottom: '1.5rem',
    },
    inputContainer: {
      position: 'relative',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '1rem 1rem 1rem 3rem',
      border: `1px solid ${theme?.borders?.primary}`,
      borderRadius: '0.75rem',
      fontSize: '1rem',
      background: theme?.background?.input || theme?.backgrounds?.input,
      color: theme?.text?.primary,
      transition: 'all 0.3s ease',
    },
    inputIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme?.text?.secondary,
      fontSize: '1.125rem',
    },
    errorText: {
      color: theme?.primary?.red?.[500] || theme?.semantic?.error,
      fontSize: '0.875rem',
      marginTop: '0.25rem',
    },
    buttonContainer: {
      marginTop: 'auto',
    },
    button: {
      width: '100%',
      padding: '1rem 2rem',
      borderRadius: '0.75rem',
      fontSize: '1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    primaryButton: {
      background: theme?.buttons?.primary?.background || `linear-gradient(135deg, ${theme?.primary?.blue?.[500]} 0%, ${theme?.primary?.blue?.[600]} 100%)`,
      color: theme?.buttons?.primary?.text || 'white',
      boxShadow: theme?.buttons?.primary?.shadow,
    },
    primaryButtonHover: {
      background: theme?.buttons?.primary?.backgroundHover || `linear-gradient(135deg, ${theme?.primary?.blue?.[600]} 0%, ${theme?.primary?.blue?.[700]} 100%)`,
      boxShadow: theme?.buttons?.primary?.shadowHover,
      transform: 'translateY(-2px)',
    },
  };

  return (
    <Formik
      initialValues={{
        first_name: firstName ?? "",
        last_name: lastName ?? "",
      }}
      enableReinitialize
      onSubmit={onSave}
      validationSchema={validationSchema}
    >
      {({
        setFieldValue,
        handleChange,
        values,
        errors,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <motion.div
            style={styles.card}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div style={styles.content}>
              <div style={styles.header}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: theme?.text?.primary,
                  marginBottom: '0.5rem'
                }}>
                  Profile Information
                </h2>
                <p style={{
                  fontSize: '0.875rem',
                  color: theme?.text?.secondary
                }}>
                  Update your personal details and preferences
                </p>
              </div>

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                  <div style={styles.inputContainer}>
                    <FontAwesomeIcon 
                      icon={faUser} 
                      style={styles.inputIcon}
                    />
                    <input
                      type="text"
                      name="first_name"
                      id="first_name"
                      onChange={handleChange}
                      value={values?.first_name}
                      placeholder="First Name"
                      style={{
                        ...styles.input,
                        ...(errors.first_name ? { borderColor: theme?.primary?.red?.[500] || theme?.semantic?.error } : {})
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = theme?.primary?.blue?.[500] || theme?.ui?.primary;
                        e.target.style.boxShadow = `0 0 0 3px ${(theme?.primary?.blue?.[500] || theme?.ui?.primary) + '20'}`;
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        e.target.style.borderColor = theme?.borders?.primary;
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="first_name"
                    component="div"
                    style={styles.errorText}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.inputContainer}>
                    <FontAwesomeIcon 
                      icon={faUser} 
                      style={styles.inputIcon}
                    />
                    <input
                      type="text"
                      name="last_name"
                      id="last_name"
                      onChange={handleChange}
                      value={values?.last_name}
                      placeholder="Last Name"
                      style={{
                        ...styles.input,
                        ...(errors.last_name ? { borderColor: theme?.primary?.red?.[500] || theme?.semantic?.error } : {})
                      }}
                      onFocus={(e) => {
                        e.target.style.outline = 'none';
                        e.target.style.borderColor = theme?.primary?.blue?.[500] || theme?.ui?.primary;
                        e.target.style.boxShadow = `0 0 0 3px ${(theme?.primary?.blue?.[500] || theme?.ui?.primary) + '20'}`;
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        e.target.style.borderColor = theme?.borders?.primary;
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="last_name"
                    component="div"
                    style={styles.errorText}
                  />
                </div>

                <div style={styles.buttonContainer}>
                  <motion.button
                    type="submit"
                    style={{
                      ...styles.button,
                      ...styles.primaryButton
                    }}
                    whileHover={styles.primaryButtonHover}
                    whileTap={{ scale: 0.98 }}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faSave} />
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        );
      }}
    </Formik>
  );
};

export default ProfileInfo;
